import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { Breadcrumbs, Crumb } from '@carsys/ui'
import { formatEnum } from '@cbgms/base/utils/helpers'
import { UUIDPathRegex } from '@cbgms/base/utils/uuid'
import { useConceptContext } from 'app/settings/components/TenderEngine/context/ConceptContext'
import { VehicleSalesInitialStockURL } from 'app/vehicleSales/RouteConstants'
import { useBreadCrumbDataContext } from 'app/crumbs/context/useSetBreadCrumbData'
import { roadAssistancePath } from 'app/settings/components/RoadAssistance/routes'
import {
  auditTrailPath,
  employeeAuditTrailPath,
  timeCorrectionEditPath,
  timeCorrectionPath
} from 'app/settings/components/TimeCorrection/routes'
import { useFeature } from '@cbgms/base/feature-flags'
import { useIsPortal } from 'utils/Subscription/PermissionSelector'

export const SettingsBreadCrumbs: React.FC<React.ComponentProps<typeof Breadcrumbs>> = ({ ...props }) => {
  const { t } = useTranslation()
  const concept = useConceptContext()
  const { breadCrumbData } = useBreadCrumbDataContext()
  const isPortal = useIsPortal()
  const hasAutomatedBreakTimeConfigurationFeature = useFeature('automated-break-time-configuration')

  const closingStatement = useSelector(state => state.settings.closingStatements.detail)
  const employee = useSelector(state => state.settings.employees.detail)
  const employeeSkill = useSelector(state => state.settings.employeeSkills.detail)
  const invoiceSequence = useSelector(state => state.settings.invoiceSequence.detail)
  const labourRate = useSelector(state => state.settings.labourRates.detail)
  const partGroup = useSelector(state => state.settings.partGroup)
  const revenueGroup = useSelector(state => state.settings.revenueGroup)
  const rdwAccount = useSelector(state => state.settings.rdwServiceProvider.detail.Name)
  const workorderActivity = useSelector(state => state.settings.workorderActivities.detail)

  const detailText = (uuid: string, value = '') => {
    return uuid === 'new' ? t('global.new') : t('global.edit_val', { value })
  }

  const invoiceSequenceIdentifier = (uuid: string) => {
    if (invoiceSequence !== null) {
      const { Year, InvoiceType } = invoiceSequence

      const year = Year ? Year.toString() : ''
      const type = formatEnum('EnumInvoiceType', InvoiceType)

      return detailText(uuid, `${type} ${year}`)
    }

    return detailText(uuid)
  }

  return (
    <Breadcrumbs {...props}>
      <Crumb data-testid='crumb-settings' key='crumb-settings' path='/settings' text={t('setting.title')} />
      <Crumb data-testid='crumb-settings-my-shop' key='crumb-settings-my-shop' path='/settings/my-shop' text={t('setting.my_shop')} />
      <Crumb
        data-testid='crumb-settings-absence-schedule'
        key='crumb-settings-absence-schedule'
        path='/settings/absence-schedule'
        text={t('setting.absence_schedule_title')}
      />
      <Crumb
        data-testid='crumb-settings-absence-schedule-view'
        key='crumb-settings-absence-schedule-view'
        path='/settings/absence-schedule/:uuid'
        text={params => detailText(params.uuid, '')}
      />
      <Crumb
        data-testid='crumb-settings-clocking-items'
        key='crumb-settings-clocking-items'
        path='/settings/clocking-items'
        text={hasAutomatedBreakTimeConfigurationFeature ? t('setting.time_registration_settings') : t('setting.clock_items')}
      />
      <Crumb
        data-testid='crumb-settings-clocking-items-view'
        key='crumb-settings-clocking-items-view'
        path='/settings/clocking-items/:uuid'
        text={params => detailText(params.uuid, breadCrumbData.clockingItemName)}
      />
      <Crumb
        data-testid='crumb-settings-vehicle-sbc'
        key='crumb-settings-vehicle-sbc'
        path='/settings/vehicle-search-by-country'
        text={t('setting_vehicle_search_by_country.page_title')}
      />
      <Crumb
        data-testid='crumb-settings-vehicle-classes'
        key='crumb-settings-vehicle-classes'
        path='/settings/vehicle-classes'
        text={t('setting.vehicle-classes')}
      />
      <Crumb
        data-testid='crumb-settings-vehicle-classes-view'
        key='crumb-settings-vehicle-classes-view'
        path='/settings/vehicle-classes/:uuid'
        text={params => detailText(params.uuid, t('setting.vehicle-classes'))}
      />
      <Crumb
        data-testid='crumb-settings-opening-hours'
        key='crumb-settings-opening-hours'
        path='/settings/opening-hours'
        text={t('setting.opening_hours')}
      />
      <Crumb
        data-testid='crumb-settings-closing-statements'
        key='crumb-settings-closing-statements'
        path='/settings/closing-statements'
        text={t('setting.closing_statements')}
      />
      <Crumb
        data-testid='crumb-settings-closing-statements-view'
        key='crumb-settings-closing-statements-view'
        path='/settings/closing-statements/:uuid'
        text={params => detailText(params.uuid, closingStatement.Title)}
      />
      <Crumb
        data-testid='crumb-settings-labour-rates'
        key='crumb-settings-labour-rates'
        path='/settings/labour-rates'
        text={t('setting.labour_rates')}
      />
      <Crumb
        data-testid='crumb-settings-labour-rates-view'
        key='crumb-settings-labour-rates-view'
        path='/settings/labour-rates/:uuid'
        text={params => detailText(params.uuid, labourRate && labourRate.Code)}
      />
      <Crumb
        data-testid='crumb-settings-payment-methods'
        key='crumb-settings-payment-methods'
        path='/settings/payment-methods'
        text={t('setting.payment_methods')}
      />
      <Crumb
        data-testid='crumb-settings-payment-methods-view'
        key='crumb-settings-payment-methods-view'
        path='/settings/payment-methods/:uuid'
        text={params => detailText(params.uuid, t('setting.payment_method'))}
      />
      <Crumb
        data-testid='crumb-settings-payment-methods-routing'
        key='crumb-settings-payment-methods-routing'
        path='/settings/payment-methods-routing'
        text={t('setting.payment_method_routing')}
      />
      <Crumb
        data-testid='crumb-settings-vat-return'
        key='crumb-settings-vat-return'
        path='/settings/vat-return'
        text={t('setting.vat_returns')}
      />
      <Crumb
        data-testid='crumb-settings-payment-methods-routing-view'
        key='crumb-settings-payment-methods-routing-view'
        path='/settings/payment-methods-routing/:uuid'
        text={params => detailText(params.uuid, t('setting.payment_method_routing'))}
      />
      <Crumb
        data-testid='crumb-settings-sms-provider'
        key='crumb-settings-sms-provider'
        path='/settings/sms-provider'
        text={t('setting.sms_provider')}
      />
      <Crumb
        data-testid='crumb-settings-concept-shop-view'
        key='crumb-settings-concept-shop-view'
        path='/settings/concepts/:conceptUUID/shop'
        text={t('TenderEngine.Title', { name: concept?.Name })}
      />
      <Crumb
        data-testid='crumb-settings-concept-rates-view'
        key='crumb-settings-concept-rates-view'
        path='/settings/concepts/:conceptUUID/rates'
        text={t('TenderEngine.RatesTitle', { name: concept?.Name })}
      />
      <Crumb
        data-testid='crumb-settings-concept-availability-view'
        key='crumb-settings-concept-availability-view'
        path='/settings/concepts/:conceptUUID/availability'
        text={t('TenderEngine.AvailabilityTitle', { name: concept?.Name })}
      />
      <Crumb
        data-testid='crumb-settings-concept-fluids-view'
        key='crumb-settings-concept-fluids-view'
        path='/settings/concepts/:conceptUUID/fluids'
        text={t('TenderEngine.FluidsTitle', { name: concept?.Name })}
      />
      <Crumb
        data-testid='crumb-settings-concept-request-iframe'
        key='crumb-settings-concept-request-iframe'
        path='/settings/concepts/:conceptUUID/request-iframe'
        text={t('Setting.Concept.RequestIframe', { name: concept?.Name })}
      />
      <Crumb
        data-testid='crumb-settings-concept-tender-lookup'
        key='crumb-settings-concept-tender-lookup'
        path='/settings/concepts/:conceptUUID/tender-lookup'
        text={t('TenderEngine.TenderLookupTitle', { name: concept?.Name })}
      />
      <Crumb
        data-testid='crumb-settings-employees'
        key='crumb-settings-employees'
        path='/settings/employees'
        text={t('setting.employees')}
      />
      <Crumb
        data-testid='crumb-settings-employees-view'
        key='crumb-settings-employees-view'
        path='/settings/employees/:uuid'
        text={params => detailText(params.uuid, `${employee.FirstName || ''} ${employee.NamePrefix || ''} ${employee.Name || ''}`)}
      />
      <Crumb
        data-testid='crumb-settings-print-order'
        key='crumb-settings-print-order'
        path='/settings/print-order'
        text={t('settings.print-order')}
      />
      <Crumb
        data-testid='crumb-settings-print-work-order'
        key='crumb-settings-print-work-order'
        path='/settings/print-work-order'
        text={t('settings.print-work-order')}
      />
      <Crumb
        data-testid='crumb-settings-lease-company'
        key='crumb-settings-lease-company'
        path='/settings/lease-company'
        text={t('setting.lease-company')}
      />
      <Crumb
        data-testid='crumb-settings-lease-company-new-rob'
        key='crumb-settings-lease-company-new-rob'
        path='/settings/lease-company/rob_nl/new'
        text={t('global.new')}
      />
      <Crumb
        data-testid='crumb-settings-lease-company-edit-rob'
        key='crumb-settings-lease-company-edit-rob'
        path='/settings/lease-company/rob_nl/update/:uuid'
        text={t('global.edit')}
      />
      <Crumb
        data-testid='crumb-settings-employee-skills'
        key='crumb-settings-employee-skills'
        path='/settings/employee-skills'
        text={t('setting.employee_skills')}
      />
      <Crumb
        data-testid='crumb-settings-employee-skills-view'
        key='crumb-settings-employee-skills-view'
        path='/settings/employee-skills/:uuid'
        text={params => detailText(params.uuid, employeeSkill.Title)}
      />
      <Crumb
        data-testid='crumb-settings-exported-invoices'
        key='crumb-settings-exported-invoices'
        path='/settings/exported-invoices'
        text={t('setting.exported_invoices')}
      />
      <Crumb
        data-testid='crumb-settings-exported-csv-view'
        key='crumb-settings-exported-csv-view'
        path='/settings/exported-invoices/csv-exports'
        text={t('setting.csv_exports')}
      />
      <Crumb
        data-testid='crumb-settings-exported-invoices-workorder_customer-view'
        key='crumb-settings-exported-invoices-workorder_customer-view'
        path='/settings/exported-invoices/invoice/workorder_customer/:uuid'
        text={`${t('invoice.invoice')}`}
      />
      <Crumb
        data-testid='crumb-settings-exported-invoices-workorder_internal-view'
        key='crumb-settings-exported-invoices-workorder_internal-view'
        path='/settings/exported-invoices/invoice/workorder_internal/:uuid'
        text={`${t('invoice.invoice')}`}
      />
      <Crumb
        data-testid='crumb-settings-exported-invoices-vehicle_purchase-view'
        key='crumb-settings-exported-invoices-vehicle_purchase-view'
        path='/settings/exported-invoices/invoice/vehicle_purchase/:uuid'
        text={`${t('invoice.invoice')}`}
      />
      <Crumb
        data-testid='crumb-settings-exported-invoices-vehicle_sale-view'
        key='crumb-settings-exported-invoices-vehicle_sale-view'
        path='/settings/exported-invoices/invoice/vehicle_sale/:uuid'
        text={`${t('invoice.invoice')}`}
      />
      <Crumb
        data-testid='crumb-settings-part-groups'
        key='crumb-settings-part-groups'
        path='/settings/part-groups'
        text={t('setting.part_groups')}
      />
      <Crumb
        data-testid='crumb-settings-part-groups-view'
        key='crumb-settings-part-groups-view'
        path='/settings/part-groups/:uuid'
        text={params => detailText(params.uuid, partGroup.Code)}
      />
      <Crumb
        data-testid='crumb-settings-default-orderaccount'
        key='crumb-settings-default-orderaccount'
        path='/settings/default-orderaccount'
        text={t('setting.default_orderaccount')}
      />
      <Crumb
        data-testid='crumb-settings-order-accounts'
        key='crumb-settings-order-accounts'
        path='/settings/order-accounts'
        text={t('setting.order_accounts')}
      />
      <Crumb
        data-testid='crumb-settings-revenue-groups'
        key='crumb-settings-revenue-groups'
        path='/settings/revenue-groups'
        text={t('setting.accountancy_revenue_groups')}
      />
      <Crumb
        data-testid='crumb-settings-revenue-groups-new-view'
        key='crumb-settings-revenue-groups-new-view'
        path='/settings/revenue-groups/new'
        text={t('setting.add_accountancy_revenue_groups')}
      />
      <Crumb
        data-testid='crumb-settings-revenue-groups-configure_defaults'
        key='crumb-settings-revenue-groups-configure_defaults'
        path='/settings/revenue-groups/configure-defaults'
        text={t('setting.accountancy_revenue_groups_configure_defaults')}
      />
      <Crumb
        data-testid='crumb-settings-revenue-groups-edit-view'
        key='crumb-settings-revenue-groups-edit-view'
        path={`/settings/revenue-groups/edit/:uuid(${UUIDPathRegex})`}
        text={params => detailText(params.uuid, revenueGroup.Name)}
      />
      <Crumb
        data-testid='crumb-settings-invoice-sequences'
        key='crumb-settings-invoice-sequences'
        path='/settings/invoice-sequences'
        text={t('setting.accountancy_invoice_sequences')}
      />
      <Crumb
        data-testid='crumb-settings-invoice-sequences-view'
        key='crumb-settings-invoice-sequences-view'
        path='/settings/invoice-sequences/:uuid'
        text={params => invoiceSequenceIdentifier(params.uuid)}
      />
      <Crumb
        data-testid='crumb-settings-administrations'
        key='crumb-settings-administrations'
        path='/settings/administrations'
        text={t('setting.accountancy_administration')}
      />
      <Crumb
        exact
        data-testid='crumb-settings-administrations-view'
        key='crumb-settings-administrations-view'
        path='/settings/administrations/:uuid'
        text={params => detailText(params.uuid)}
      />
      <Crumb
        exact
        data-testid='crumb-settings-administrations-custom'
        key='crumb-settings-administrations-custom'
        path='/settings/administrations/custom/:uuid'
        text={t('Administration.CustomSettings')}
      />
      <Crumb
        data-testid='crumb-settings-ledgers'
        key='crumb-settings-ledgers'
        path='/settings/ledgers'
        text={t('setting.revenue_ledgers')}
      />
      <Crumb
        data-testid='crumb-settings-ledgers-view'
        key='crumb-settings-ledgers-view'
        path='/settings/ledgers/:uuid'
        text={params => detailText(params.uuid)}
      />
      <Crumb
        data-testid='crumb-settings-debit-credit-numbers'
        key='crumb-settings-debit-credit-numbers'
        path='/settings/debit-credit-numbers'
        text={t('setting.debtor_creditor_numbers')}
      />
      <Crumb
        data-testid='crumb-settings-workorder-activities'
        key='crumb-settings-workorder-activities'
        path='/settings/workorder-activities'
        text={t('setting.workorder_activities')}
      />
      <Crumb
        data-testid='crumb-settings-workorder-activities-view'
        key='crumb-settings-workorder-activities-view'
        path='/settings/workorder-activities/:uuid'
        text={params => detailText(params.uuid, workorderActivity?.Code)}
      />
      <Crumb
        data-testid='crumb-settings-workorder-packages'
        key='crumb-settings-workorder-packages'
        path='/settings/workorder-packages'
        text={t('setting.workorder_packages')}
      />
      <Crumb
        data-testid='crumb-settings-workorder-packages-view'
        key='crumb-settings-workorder-packages-view'
        path='/settings/workorder-packages/:uuid'
        text={params => detailText(params.uuid)}
      />
      <Crumb
        data-testid='crumb-settings-workorder-numbers'
        key='crumb-settings-workorder-numbers'
        path='/settings/workorder-numbers'
        text={t('setting.workorder_numbers')}
      />
      <Crumb
        data-testid='crumb-journal-entry-numbers'
        key='crumb-journal-entry-numbers'
        path='/settings/journal-entry-numbers'
        text={t('setting_journal_entry_numbers.booking_numbers')}
      />
      <Crumb
        data-testid='crumb-settings-workorder-quotations'
        key='crumb-settings-workorder-quotations'
        path='/settings/workorder-quotations'
        text={t('setting.workorder_quotations.quotations')}
      />
      <Crumb
        data-testid='crumb-settings-order-quotations'
        key='crumb-settings-order-quotations'
        path='/settings/order-quotations'
        text={t('setting.workorder_quotations.quotations')}
      />
      <Crumb data-testid='crumb-settings-media' key='crumb-settings-media' path='/settings/media' text={t('setting.media')} />
      <Crumb
        data-testid='crumb-settings-email-templates'
        key='crumb-settings-email-templates'
        path='/settings/email-templates'
        text={t('setting.email_templates')}
      />
      <Crumb
        data-testid='crumb-settings-invoice-layout'
        key='crumb-settings-invoice-layout'
        path='/settings/invoice-layout'
        text={t('setting.invoice_layout')}
      />
      <Crumb data-testid='crumb-settings-sundries' key='crumb-settings-sundries' path='/settings/sundries' text={t('setting.sundries')} />
      <Crumb
        data-testid='crumb-settings-price-markups'
        key='crumb-settings-price-markups'
        path='/settings/price-markups'
        text={isPortal ? t('setting.PriceMarkupsBeta') : t('setting.PriceMarkups')}
      />
      <Crumb
        data-testid='crumb-settings-tyre-change'
        key='crumb-settings-tyre-change'
        path='/settings/tyre-change'
        text={t('setting.TyreChange')}
      />
      <Crumb
        data-testid='crumb-settings-rdw-service-provider-accounts'
        key='crumb-settings-rdw-service-provider-accounts'
        path='/settings/rdw-service-provider-accounts'
        text={t('setting.rdw_service_provider_accounts')}
      />
      <Crumb
        data-testid='crumb-settings-rdw-service-provider-A2SP-accounts'
        key='crumb-settings-rdw-service-provider-A2SP-accounts'
        path='/settings/rdw-service-provider-accounts/new/A2SP'
        text={t('Enum.EnumRDWProviderSystem_A2SP')}
      />
      <Crumb
        data-testid='crumb-settings-rdw-service-provider-RDC-accounts'
        key='crumb-settings-rdw-service-provider-RDC-accounts'
        path='/settings/rdw-service-provider-accounts/new/RDC'
        text={t('Enum.EnumRDWProviderSystem_RDC')}
      />
      <Crumb
        data-testid='crumb-settings-rdw-service-provider-VWE-accounts'
        key='crumb-settings-rdw-service-provider-VWE-accounts'
        path='/settings/rdw-service-provider-accounts/new/VWE'
        text={t('Enum.EnumRDWProviderSystem_VWE')}
      />
      <Crumb
        data-testid='crumb-settings-rdw-service-provider-accounts-view'
        key='crumb-settings-rdw-service-provider-accounts-view'
        path='/settings/rdw-service-provider-accounts/edit/:uuid'
        text={params => detailText(params.uuid, rdwAccount)}
      />
      <Crumb
        data-testid='crumb-settings-technical-data-settings'
        key='crumb-settings-technical-data-settings'
        path='/settings/technical-data-settings'
        text={t('setting.haynes_pro_page_title')}
      />
      <Crumb
        data-testid='crumb-settings-third_party_licenses'
        key='crumb-settings-third_party_licenses'
        path='/settings/third-party-licenses'
        text={t('setting.third_party_licenses')}
      />
      <Crumb
        data-testid='crumb-settings-import-data'
        key='crumb-settings-import-data'
        path='/settings/import-data'
        text={t('setting.ImportDataTitle')}
      />
      <Crumb data-testid='crumb-settings-planner' key='crumb-settings-planner' path='/settings/planner' text={t('setting.planner')} />
      <Crumb
        data-testid='crumb-settings-workstations'
        key='crumb-settings-workstations'
        path='/settings/workstations'
        text={t('setting.workstations')}
      />
      <Crumb
        data-testid='crumb-settings-workstations-view'
        key='crumb-settings-workstations-view'
        path='/settings/workstations/:uuid'
        text={params => detailText(params.uuid, breadCrumbData.workstationName || '')}
      />
      <Crumb
        data-testid='crumb-settings-vehicle-sales-trade-in'
        key='crumb-settings-vehicle-sales-trade-in'
        path='/settings/vehicle-sales-trade-in'
        text={t('setting.vehicle_sales.trade_in')}
      />
      <Crumb
        data-testid='crumb-settings-vehicle-sales-quotations'
        key='crumb-settings-vehicle-sales-quotations'
        path='/settings/vehicle-sales-quotations'
        text={t('setting.vehicle_sales.quotations')}
      />
      <Crumb
        data-testid='crumb-settings-vehicle-sales-initial-stock'
        key='crumb-settings-vehicle-sales-initial-stock'
        path={VehicleSalesInitialStockURL}
        text={t('setting.initial-vehicle-stock')}
      />
      <Crumb
        data-testid='crumb-settings-vehicle-sales-vehicle-warranties'
        key='crumb-settings-vehicle-sales-vehicle-warranties'
        path='/settings/vehicle-warranties'
        text={t('setting.warranties')}
      />
      <Crumb
        data-testid='crumb-settings-vehicle-sales-vehicle-warranties-view'
        key='crumb-settings-vehicle-sales-vehicle-warranties-view'
        path='/settings/vehicle-warranties/:uuid'
        text={params => detailText(params.uuid, '')}
      />
      <Crumb
        data-testid='crumb-settings-example-booking'
        key='crumb-settings-example-booking'
        path='/settings/exported-invoices/booking'
        text={t('invoice.invoice')}
      />
      <Crumb data-testid='crumb-settings-mykar' key='crumb-settings-mykar' path='/settings/mykar' text={t('setting.mykar.title')} />
      {/*
        There is only a mollie payment page at the time of this refactor.
      <Crumb
        data-testid='settings-payment-providers'
        key='settings-payment-providers'
        path='/settings/payment-providers'
        text={t('setting.payment-providers.title')}
      /> */}
      <Crumb
        data-testid='settings-payment-providers-mollie'
        key='settings-payment-providers-mollie'
        path='/settings/payment-providers/mollie'
        text={t('setting.payment-providers.mollie.title')}
      />
      <Crumb
        data-testid='settings-catalog-settings'
        key='settings-catalog-settings'
        path='/settings/catalog-settings'
        text={t('setting.catalog_settings')}
      />
      <Crumb
        data-testid='settings-scheduler-settings'
        key='settings-scheduler-settings'
        path='/settings/scheduler'
        text={t('setting.Scheduler')}
      />
      <Crumb
        data-testid='settings-cookie-settings'
        key='settings-cookie-settings'
        path='/settings/cookie-settings'
        text={t('legal.cookie_settings.cookie_title')}
      />
      <Crumb
        data-testid='settings-privacy-and-conditions-settings'
        key='settings-privacy-and-conditions-settings'
        path='/settings/privacy-and-conditions'
        text={t('legal.privacy_settings.privacy_title')}
      />
      <Crumb
        data-testid='settings-capture-external-gms-settings'
        key='settings-capture-external-gms-settings'
        path='/settings/capture-external-gms'
        text={t('setting_capture_external_gms.titleWithAppName')}
      />
      <Crumb
        data-testid='settings-departments-settings'
        key='settings-departments-settings'
        path='/settings/departments'
        text={t('setting.departments')}
      />
      <Crumb
        data-testid='settings-departments-new-view'
        key='settings-departments-new-view'
        path='/settings/departments/new'
        text={t('global.add')}
      />
      <Crumb
        data-testid='settings-departments-edit-view'
        key='settings-departments-edit-view'
        path='/settings/departments/edit/:uuid'
        text={t('global.edit')}
      />
      <Crumb data-testid='settings-expense-of' key='settings-expense-of' path='/settings/expense-of' text={t('setting.expense_of')} />
      <Crumb
        data-testid='settings-expense-of-new-view'
        key='settings-expense-of-new-view'
        path='/settings/expense-of/new'
        text={t('global.new')}
      />
      <Crumb
        data-testid='settings-expense-of-edit-view'
        key='settings-expense-of-edit-view'
        path='/settings/expense-of/edit/:expenseOfUUID'
        text={t('global.edit')}
      />
      <Crumb
        data-testid='settings-order-returns'
        key='settings-order-returns'
        path='/settings/order-returns'
        text={t('setting.workorder_returns.returns-page-title')}
      />
      <Crumb
        data-testid='settings-workorder-returns'
        key='settings-workorder-returns'
        path='/settings/workorder-returns'
        text={t('setting.workorder_returns.returns-page-title')}
      />
      <Crumb
        data-testid='settings-order-line-reference'
        key='settings-order-line-reference'
        path='/settings/order-line-reference'
        text={t('setting.order_line_reference.title')}
      />
      <Crumb
        data-testid='settings-oauth-third-parties'
        key='settings-oauth-third-parties'
        path='/settings/oauth-third-parties'
        text={t('setting_oauth_third_parties.title')}
      />
      <Crumb
        data-testid='settings-vehicle-sales-line-templates'
        key='settings-vehicle-sales-line-templates'
        path='/settings/vehicle-sales-line-templates'
        text={t('setting.linetemplates')}
      />
      <Crumb
        data-testid='settings-vehicle-sales-line-templates-edit-view'
        key='settings-vehicle-sales-line-templates-edit-view'
        path='/settings/vehicle-sales-line-templates/edit/:uuid'
        text={t('global.edit')}
      />
      <Crumb
        data-testid='settings-vehicle-sales-line-templates-new-view'
        key='settings-vehicle-sales-line-templates-new-view'
        path='/settings/vehicle-sales-line-templates/new'
        text={t('global.new')}
      />
      <Crumb
        data-testid='settings-workorder-default-custom-lines'
        key='settings-workorder-default-custom-lines'
        path='/settings/workorder-default-custom-lines'
        text={t('setting.workorder-default-custom-lines.title')}
      />
      <Crumb
        data-testid='settings-workorder-default-custom-lines-new-view'
        key='settings-workorder-default-custom-lines-new-view'
        path='/settings/workorder-default-custom-lines/new'
        text={t('global.new')}
      />
      <Crumb
        data-testid='settings-workorder-default-custom-lines-edit-view'
        key='settings-workorder-default-custom-lines-edit-view'
        path='/settings/workorder-default-custom-lines/edit/:uuid'
        text={t('global.edit')}
      />
      <Crumb
        data-testid='settings-workorder-default-text-lines'
        key='settings-workorder-default-text-lines'
        path='/settings/workorder-default-text-lines'
        text={t('setting.workorder-default-text-lines.title')}
      />
      <Crumb
        data-testid='settings-workorder-default-text-lines-new-view'
        key='settings-workorder-default-text-lines-new-view'
        path='/settings/workorder-default-text-lines/new'
        text={t('global.new')}
      />
      <Crumb
        data-testid='settings-workorder-default-text-lines-edit-view'
        key='settings-workorder-default-text-lines-edit-view'
        path='/settings/workorder-default-text-lines/edit/:uuid'
        text={t('global.edit')}
      />
      <Crumb
        data-testid='settings-archive-external-edit-view'
        key='settings-archive-external-edit-view'
        path='/archive-external/:uuid'
        text={t('archive-external.detail.title')}
      />
      <Crumb
        data-testid='settings-survey-providers'
        key='settings-survey-providers'
        path='/settings/survey-providers'
        text={t('setting.survey-providers.title')}
      />
      <Crumb
        data-testid='settings-survey-providers-new-view'
        key='settings-survey-providers-new-view'
        path='/settings/survey-providers/new'
        text={t('global.add')}
      />
      <Crumb
        data-testid='settings-survey-providers-edit-view'
        key='settings-survey-providers-edit-view'
        path='/settings/survey-providers/edit/:providerType/:uuid'
        text={t('global.edit')}
      />
      <Crumb
        data-testid='settings-road-assistance-providers'
        key='settings-road-assistance-providers'
        path={roadAssistancePath}
        text={t('setting.road-assistance.title')}
      />
      <Crumb
        data-testid='settings-time-correction-providers'
        key='settings-time-correction-providers'
        path={timeCorrectionPath}
        text={t('setting.time_correction.title')}
      />
      <Crumb
        data-testid='settings-time-correction-edit'
        key='settings-time-correction-edit'
        path={timeCorrectionEditPath}
        text={t('setting.time_correction.attendance_correction')}
      />
      <Crumb
        data-testid='settings-time-correction-employee-audit-trail'
        key='settings-time-correction-audit-employee-trail'
        path={employeeAuditTrailPath}
        text={t('setting.time_correction.audit_trail')}
      />
      <Crumb
        data-testid='settings-time-correction-audit-trail'
        key='settings-time-correction-audit-trail'
        path={auditTrailPath}
        text={t('setting.time_correction.audit_trail')}
      />
      <Crumb data-testid='settings-wom-plus' key='settings-wom-plus' path='/settings/wom-plus' text={t('setting.womplus.title')} />
      <Crumb
        data-testid='settings-external-websites'
        key='settings-external-websites'
        path='/settings/external-websites'
        text={t('setting.external-websites.title')}
      />
      <Crumb
        data-testid='settings-external-websites-new'
        key='settings-external-websites-new-view'
        path='/settings/external-websites/new'
        text={t('global.new')}
      />
      <Crumb
        data-testid='settings-external-websites-edit'
        key='settings-external-websites-edit-view'
        path='/settings/external-websites/edit/:uuid'
        text={t('global.edit')}
      />
      <Crumb data-testid='settings-bcc-email' key='settings-bcc-email' path='/settings/bcc-email' text={t('setting.email-settings')} />
    </Breadcrumbs>
  )
}

export default SettingsBreadCrumbs
